// src/dashboard/pages/teacher/Techniques/TechniquesDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useTechniquesDashboard } from "./hooks/useTechniquesDashboard";

import DashboardTechniquesHeader from "./components/DashboardTechniquesHeader";
import TechniqueFiltersCard from "./components/TechniqueFiltersCard";
import TechniqueKpiCards from "./components/TechniqueKpiCards";
import TechniqueDataTable from "./components/TechniqueDataTable";
import TechniqueMobileCards from "./components/TechniqueMobileCards";
import TechniqueEmptyState from "./components/TechniqueEmptyState";
import AddTechniqueDrawer from "./components/AddTechniqueDrawer";
import EditTechniqueDrawer from "./components/EditTechniqueDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Techniques
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux enseignants et administrateurs du
              club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function TechniquesDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    techniques,
    filteredTechniques,
    familyOptions,
    beltOptions,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,

    addDrawerOpen,
    editDrawerOpen,
    currentTechnique,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreateTechnique,
    handleUpdateTechnique,
    handleDeleteTechnique,
    handleUploadTechniquePhoto,
    refreshTechniques,
  } = useTechniquesDashboard();

  if (![1, 3].includes(roleId)) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardTechniquesHeader />

        <TechniqueFiltersCard
          filters={filters}
          setFilters={setFilters}
          familyOptions={familyOptions}
          beltOptions={beltOptions}
          loading={loading}
          onAdd={openAddDrawer}
          onRefresh={refreshTechniques}
        />

        <TechniqueKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Bibliothèque technique
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Techniques, familles et ceintures
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Consultez et gérez les techniques du club, leurs familles,
                  leur niveau de ceinture, leurs visuels et leurs contenus
                  pédagogiques.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Techniques affichées :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredTechniques.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {techniques.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredTechniques.length === 0 ? (
              <TechniqueEmptyState onAdd={openAddDrawer} />
            ) : (
              <div className="mt-5">
                <TechniqueDataTable
                  items={filteredTechniques}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteTechnique}
                  deletingId={deletingId}
                />

                <TechniqueMobileCards
                  items={filteredTechniques}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteTechnique}
                  deletingId={deletingId}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <AddTechniqueDrawer
        isOpen={addDrawerOpen}
        onClose={closeAddDrawer}
        onSubmit={handleCreateTechnique}
        saving={saving}
        familyOptions={familyOptions}
        beltOptions={beltOptions}
      />

      <EditTechniqueDrawer
        isOpen={editDrawerOpen}
        onClose={closeEditDrawer}
        technique={currentTechnique}
        onSubmit={handleUpdateTechnique}
        onDelete={handleDeleteTechnique}
        onUploadPhoto={handleUploadTechniquePhoto}
        saving={saving}
        deletingId={deletingId}
        familyOptions={familyOptions}
        beltOptions={beltOptions}
      />

      <ToastContainer />
    </section>
  );
}